import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import LoanRequestDialog from '../../components/Dialog/LoanRequestDialog';
import * as Slider from '@radix-ui/react-slider';

const EligibilityChecker = () => {
    const [grossIncome, setGrossIncome] = useState(10000);
    const [tenure, setTenure] = useState(30);
    const [interestRate, setInterestRate] = useState(5.25);
    const [otherEMIs, setOtherEMIs] = useState(0);

    const [homeLoanEligibility, setHomeLoanEligibility] = useState(0);
    const [homeLoanEMI, setHomeLoanEMI] = useState(0);

    const calculateEligibility = () => {
        const monthlyIncome = parseFloat(grossIncome);
        const totalEMIs = parseFloat(otherEMIs);
        
        const maxEMI = (monthlyIncome * 0.7); 
        const monthlyInterestRate = parseFloat(interestRate) / 100 / 12;
        const numMonths = parseFloat(tenure) * 12;

        let loanAmount = 0;
        if (monthlyInterestRate > 0) {
            loanAmount = (maxEMI - totalEMIs) * (
                (Math.pow(1 + monthlyInterestRate, numMonths) - 1) /
                (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numMonths))
            );
        } else {
            loanAmount = (maxEMI - totalEMIs) * numMonths;
        }

        const emi = (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numMonths)) / 
                    (Math.pow(1 + monthlyInterestRate, numMonths) - 1);

        setHomeLoanEligibility(loanAmount);
        setHomeLoanEMI(emi);
    };

    useEffect(() => {
        calculateEligibility();
    }, [grossIncome, tenure, interestRate, otherEMIs]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="font-sans min-h-screen bg-white">
            <div className="flex flex-col lg:flex-row gap-8 w-full max-w-6xl mx-auto p-4 lg:p-0">
                {/* Input Section */}
                <div className="flex-1 ">
                    <div>
                        <label className="block text-sm text-gray-600">Gross Income (Monthly)</label>
                        <div className="flex items-center border-b-2 border-gray-300 py-2">
                            <span className="text-gray-500 text-base mr-1">₹</span>
                            <input 
                                type="number" 
                                value={grossIncome} 
                                onChange={(e) => setGrossIncome(parseFloat(e.target.value))} 
                                className="w-full text-base border-none outline-none text-gray-800 bg-transparent"
                            />
                        </div>
                        <Slider.Root 
                            className="relative flex items-center select-none touch-none w-full h-5 mt-2" 
                            defaultValue={[grossIncome]} 
                            max={10000000} 
                            min={10000}
                            step={1000}
                            onValueChange={(value) => setGrossIncome(value[0])}
                        >
                            <Slider.Track className="bg-red-500/40 relative flex-grow rounded-full h-1.5">
                                <Slider.Range className="absolute bg-red-500 rounded-full h-full" />
                            </Slider.Track>
                            <Slider.Thumb 
                            className="block w-4 h-4 bg-accent border-2 border-white rounded-full shadow-lg hover:bg-primary-700 focus:outline-none " 
                             aria-label="Gross Income" 
                            />
                        </Slider.Root>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>₹ 10 K</span>
                            <span>₹ 1 Cr</span>
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm text-gray-600">Tenure (Years)</label>
                        <div className="flex items-center border-b-2 border-gray-300 py-1">
                            <input 
                                type="number" 
                                value={tenure} 
                                onChange={(e) => setTenure(parseFloat(e.target.value))} 
                                className="w-full text-base border-none outline-none text-gray-800 bg-transparent"
                            />
                        </div>
                        <Slider.Root 
                            className="relative flex items-center select-none touch-none w-full h-5 mt-2" 
                            defaultValue={[tenure]} 
                            max={30} 
                            min={1}
                            step={1}
                            onValueChange={(value) => setTenure(value[0])}
                        >
                            <Slider.Track className="bg-red-500/40 relative flex-grow rounded-full h-1.5">
                                <Slider.Range className="absolute bg-red-500 rounded-full h-full" />
                            </Slider.Track>
                            <Slider.Thumb 
                                className="block w-4 h-4 bg-accent border-2 border-white rounded-full shadow-lg hover:bg-primary-700 focus:outline-none " 
                                aria-label="Tenure" 
                            />
                        </Slider.Root>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>1</span>
                            <span>30</span>
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm text-gray-600">Interest Rate (% P.A.)</label>
                        <div className="flex items-center border-b-2 border-gray-300 py-1">
                            <input 
                                type="number" 
                                value={interestRate} 
                                onChange={(e) => setInterestRate(parseFloat(e.target.value))} 
                                className="w-full text-base border-none outline-none text-gray-800 bg-transparent"
                            />
                        </div>
                        <Slider.Root 
                            className="relative flex items-center select-none touch-none w-full h-5 mt-2" 
                            defaultValue={[interestRate]} 
                            max={15} 
                            min={3.5}
                            step={0.01}
                            onValueChange={(value) => setInterestRate(value[0])}
                        >
                            <Slider.Track className="bg-red-500/40 relative flex-grow rounded-full h-1.5">
                                <Slider.Range className="absolute bg-red-500 rounded-full h-full" />
                            </Slider.Track>
                            <Slider.Thumb 
                                className="block w-4 h-4 bg-accent border-2 border-white rounded-full shadow-lg hover:bg-primary-700 focus:outline-none " 
                                 aria-label="Interest Rate" 
                            />
                        </Slider.Root>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>3.5</span>
                            <span>15</span>
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm text-gray-600">Other EMIs (Monthly)</label>
                        <div className="flex items-center border-b-2 border-gray-300 py-1">
                            <span className="text-gray-500 text-base mr-1">₹</span>
                            <input 
                                type="number" 
                                value={otherEMIs} 
                                onChange={(e) => setOtherEMIs(parseFloat(e.target.value))} 
                                className="w-full text-base border-none outline-none text-gray-800 bg-transparent"
                                max={grossIncome}
                            />
                        </div>
                        <Slider.Root 
                            className="relative flex items-center select-none touch-none w-full h-5 mt-2" 
                            defaultValue={[otherEMIs]} 
                            max={grossIncome} 
                            min={0}
                            step={1000}
                            onValueChange={(value) => setOtherEMIs(value[0])}
                        >
                            <Slider.Track className="bg-red-500/40 relative flex-grow rounded-full h-1.5">
                                <Slider.Range className="absolute bg-red-500 rounded-full h-full" />
                            </Slider.Track>
                            <Slider.Thumb 
                                className="block w-4 h-4 bg-accent border-2 border-white rounded-full shadow-lg hover:bg-primary-700 focus:outline-none " 
                                aria-label="Other EMIs" 
                            />
                        </Slider.Root>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>₹ 0</span>
                            <span>{formatCurrency(grossIncome)}</span>
                        </div>
                    </div>
                </div>

                {/* Result Section with Framer Motion */}
                <motion.div 
                    className="flex-1 flex flex-col items-start lg:items-center justify-center p-4 lg:p-8"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                >
                    <div className="mb-6">
                        <div className="text-gray-600 text-sm mb-1">Your Home Loan Eligibility</div>
                        <motion.div 
                            className="text-accent text-4xl font-bold"
                            key={homeLoanEligibility}
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            {formatCurrency(homeLoanEligibility)}
                        </motion.div>
                    </div>

                    <div className="mb-6">
                        <div className="text-gray-600 text-sm mb-1">Your Home Loan EMI will be</div>
                        <motion.div 
                            className="text-accent text-3xl font-bold"
                            key={homeLoanEMI}
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            {formatCurrency(homeLoanEMI)}
                            <span className="text-base font-normal text-gray-500"> /monthly</span>
                        </motion.div>
                    </div>

                    {/* Assistance Button Section */}
                    <div className="mt-4 text-center">
                        <div className="text-sm font-semibold text-gray-700 mb-2">Do you need any assistance? Would you like to know more about the loan?</div>
                        <LoanRequestDialog>
                            <button className="bg-accent text-white font-semibold py-2 px-4 rounded-md text-sm hover:bg-primary-700 transition-colors">
                                Get Assistance
                            </button>
                        </LoanRequestDialog>
                    </div>
                    
                    <div className="mt-6 text-xs text-gray-400">
                        These calculators are provided only as general self-help Planning Tools. Results depend on many factors, including the assumptions you provide. We do not guarantee their accuracy, or applicability to your circumstances.
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default EligibilityChecker;